import { PackagePrices } from 'types/join';

export const allAvailablePlans = [
  'Rapaport Trade Dealer',
  'Rapaport Trade Buyer',
  'Rapaport Trade Buyer India',
  'Rapaport Trade Dealer Lite',
  'Rapaport Trade Primary Supplier',
  'Rapaport Trade Gem Dealer',
  'Rapaport Trade Gem Buyer',
  'Rapaport Basic',
  'Rapaport Plus',
  'Rapaport Intelligence Report'
];
export const allAvailableAddons = [
  'Member Addon',
  'Rapaport Magazine',
  'Rapaport Intelligence Report add-on',
  'Instant Inventory',
  'Rapaport Price List Plus',
  'Rapaport Price List'
];

export const priceListPlans = ['Rapaport Basic', 'Rapaport Plus', 'Rapaport Intelligence Report'];

export const steps = ['Details', 'Verification', 'Complete Registration'];

export const getPlanColor = (plan?: string) => {
  return plan ? '#000000' : '';
};

const PLAN_BRAND_PREFIXES = ['Rapaport Trade', 'Rapaport'] as const;

/**
 * Splits a plan package name into its brand prefix and plan type.
 *
 * Examples:
 *   "Rapaport Trade Dealer"      -> { brand: "Rapaport Trade", type: "Dealer" }
 *   "Rapaport Trade Buyer India" -> { brand: "Rapaport Trade", type: "Buyer India" }
 *   "Rapaport Intelligence Report"   -> { brand: "Rapaport", type: "Intelligence Report" }
 *   "Rapaport Basic"             -> { brand: "Rapaport", type: "Basic" }
 *
 * Falls back to treating the entire name as the brand when no known
 * prefix is detected, so unknown packages still render gracefully.
 */
export const splitPlanName = (packageName?: string): { brand: string; type: string } => {
  const name = (packageName ?? '').trim();
  if (!name) return { brand: '', type: '' };

  const prefix = PLAN_BRAND_PREFIXES.find(p => name === p || name.startsWith(`${p} `));

  if (!prefix) return { brand: name, type: '' };

  return {
    brand: prefix,
    type: name.slice(prefix.length).trim()
  };
};

export const getAddonName = (addon: PackagePrices) => {
  if (addon.packageName === 'Member Addon') return `Additional User `;
  if (addon.packageName === 'Rapaport Intelligence Report add-on')
    return `Rapaport Intelligence Report`;
  if (addon.packageName === 'Rapaport Price List Plus') return `Rapaport Plus`;
  if (addon.packageName === 'Rapaport Price List') return `Rapaport Member`;
  else return addon.packageName;
};
export const getPlanText = (packageName: string) => {
  if (packageName.includes('Buyer')) return 'rapnet_buyer_text';
  if (packageName.includes('Buyer India')) return 'rapnet_buyer_india_text';
  if (packageName.includes('Dealer Lite') || packageName.includes('Dealer Lite India'))
    return 'rapnet_buyer_pro_text';
  if (packageName.includes('Dealer')) return 'rapnet_dealer_text';
  if (packageName.includes('Primary Supplier')) return 'rapnet_primary_supplier_text';
  if (packageName.includes('Gem Dealer')) return 'rapnet_gem_dealer_text';
  if (packageName.includes('Gem Buyer')) return 'rapnet_gem_buyer_text';
  if (packageName === 'Rapaport Basic') return 'rapaport_monthly_price_list_text';
  if (packageName === 'Rapaport Plus') return 'rapaport_weekly_price_list_text';
  if (packageName === 'Rapaport Intelligence Report') return 'rapaport_intelligence_report_text';
};

export const getAddonText = (packageName: string) => {
  if (packageName === 'Rapaport Magazine') return 'rapaport_magazine_tooltip';
  if (packageName === 'Member Addon') return 'additional_user_tooltip';
  if (packageName === 'Instant Inventory') return 'instant_inventory_tooltip';
  if (packageName === 'Rapaport Intelligence Report add-on')
    return 'rapaport_research_report_adoon_tooltip';
  if (packageName === 'Rapaport Price List Plus') return 'rapaport_plus_tooltip';
  if (packageName === 'Rapaport Price List') return 'rapaport_member_tooltip';
};

export const planNameToPackageId = (
  planText: string,
  companyCountry: string,
  packagePrices: PackagePrices[] | null
) => {
  const plan = planText?.toLowerCase().replaceAll(/[\s_]/g, '');

  if (
    plan.includes('rapaporttradebuyerpro') ||
    plan.includes('rapaporttradedealerlite') ||
    plan.includes('rapnetbuyerpro') ||
    plan.includes('rapnetdealerlite')
  )
    return (
      packagePrices?.find(
        p => p.packageName.includes('Dealer Lite') && !p.packageName.includes('India')
      )?.packageId || ''
    );

  if (
    (plan.includes('rapaporttradedealer') || plan.includes('rapnetdealer')) &&
    !plan.includes('gem')
  )
    return (
      packagePrices?.find(
        p =>
          p.packageName.includes('Dealer') &&
          !p.packageName.includes('Gem') &&
          !p.packageName.includes('Platinum') &&
          !p.packageName.includes('Lite')
      )?.packageId || ''
    );

  if (plan.includes('rapaporttradebuyerindia') || plan.includes('rapnetbuyerindia'))
    return packagePrices?.find(p => p.packageName.includes('Buyer India'))?.packageId || '';

  if (
    (plan.includes('rapaporttradebuyer') || plan.includes('rapnetbuyer')) &&
    !plan.includes('gem')
  )
    return (
      packagePrices?.find(
        p =>
          p.packageName.includes('Buyer') &&
          !p.packageName.includes('Gem') &&
          !p.packageName.includes('India') &&
          !p.packageName.includes('Platinum')
      )?.packageId || ''
    );

  if (plan.includes('rapaporttradeprimarysupplier') || plan.includes('rapnetprimarysupplier'))
    return (
      packagePrices?.find(
        p =>
          p.packageName.includes('Primary Supplier') &&
          !p.packageName.includes('Platinum') &&
          !p.packageName.includes('Additional Office')
      )?.packageId || ''
    );

  if (plan.includes('rapaporttradegemdealer') || plan.includes('rapnetgemdealer'))
    return packagePrices?.find(p => p.packageName.includes('Gem Dealer'))?.packageId || '';

  if (plan.includes('rapaporttradegembuyer') || plan.includes('rapnetgembuyer'))
    return packagePrices?.find(p => p.packageName.includes('Gem Buyer'))?.packageId || '';

  if (plan === 'pricesheetmonthly' || plan === 'rapaportbasic')
    return packagePrices?.find(p => p.packageName === 'Rapaport Basic')?.packageId || '';

  if (plan === 'pricesheetweekly' || plan === 'rapaportplus')
    return packagePrices?.find(p => p.packageName === 'Rapaport Plus')?.packageId || '';

  if (plan === 'rapaportintelligencereport' || plan === 'rapaportresearchreport')
    return (
      packagePrices?.find(p => p.packageName === 'Rapaport Intelligence Report')?.packageId || ''
    );

  return '';
};

export const foundValue = (array: number[], value: number) => {
  let isFound = false;
  array.forEach(el => {
    if (el === value) {
      isFound = true;
    }
  });
  return isFound;
};

export const getPriceListPackageName = (planText: string) => {
  const plan = planText?.toLowerCase().replaceAll(/[\s_]/g, '');
  if (plan === 'pricesheetmonthly' || plan === 'rapaportbasic') return 'Rapaport Basic';
  if (plan === 'pricesheetweekly' || plan === 'rapaportplus') return 'Rapaport Plus';
  if (plan === 'rapaportintelligencereport') return 'Rapaport Intelligence Report';

  return planText;
};

export const onlyYearlyPlans = (packageName: string) => {
  return (
    packageName.includes('Gem Dealer') ||
    packageName.includes('Gem Buyer') ||
    packageName.includes('Buyer India') ||
    packageName === 'Rapaport Basic' ||
    packageName === 'Rapaport Plus' ||
    packageName === 'Rapaport Intelligence Report'
  );
};

/**
 * Formats addon names for display
 * @param addon - The addon package
 * @param selectedAddons - All selected addons for context
 * @returns Formatted addon name
 */
export const getAddonDisplayName = (
  addon: PackagePrices | undefined,
  selectedAddons: (PackagePrices | undefined)[] | undefined
): string => {
  if (addon === undefined) return '';

  if (addon.packageName === 'Member Addon') return `Additional User X ${addon.addonCount}`;
  if (addon.packageName === 'Rapaport Intelligence Report add-on')
    return `Rapaport Intelligence Report`;
  if (addon.packageName === 'Rapaport Price List Plus') return `Rapaport Plus`;
  if (addon.packageName === 'Rapaport Price List') return `Rapaport Member`;
  if (
    addon.packageName === 'Complementary Member' &&
    selectedAddons?.filter(a => a?.packageName === 'Complementary Member').length === 2
  ) {
    return 'Complimentary Members x 2';
  }

  return addon.packageName;
};

/**
 * Calculates the total price from selected plan and addons
 * @param selectedPlan - The selected main plan
 * @param selectedAddons - Array of selected addons
 * @returns Total price
 */
export const calculateTotalPrice = (
  selectedPlan: PackagePrices | undefined,
  selectedAddons: (PackagePrices | undefined)[] | undefined
): number => {
  let totalPrice = selectedPlan?.price ?? 0;
  selectedAddons?.forEach(addon => {
    totalPrice += (addon?.price ?? 0) * (addon?.addonCount ?? 0);
  });
  return totalPrice;
};
